import { createFileRoute, Navigate, useLocation, useMatch } from '@tanstack/react-router'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import * as v from 'valibot'
import { Button } from '@/components/ui/button'
import { useSessionUser } from '@/hooks/use-session'
import { pageSeo } from '@/lib/seo'
import { errorCodeMessage, trpc } from '@/main'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { AuthCardSkeleton } from '@/routes/_app/-components/app-layout-skeleton'

/**
 * The consent page an external AI app (an MCP client) sends its user to: it names the app, where it sends them back
 * and the context it will act in, and Approve or Deny hands the browser back to the app — docs/notes/server/mcp.md.
 */
export const Route = createFileRoute('/_public/oauth/authorize')({
  // OAuth's own names; each one the server checks against the registered client.
  validateSearch: v.object({
    client_id: v.optional(v.string(), ''),
    redirect_uri: v.optional(v.string(), ''),
    code_challenge: v.optional(v.string(), ''),
    code_challenge_method: v.optional(v.string(), ''),
    state: v.optional(v.string()),
    resource: v.optional(v.string()),
  }),
  head: (ctx) =>
    pageSeo(ctx, {
      title: msg`Connect an AI app`,
      description: msg`Allow an AI app to use your D’Anchiano data.`,
      noindex: true,
    }),
  component: RouteComponent,
  pendingComponent: () => <AuthCardSkeleton logo fields={3} />,
})

function RouteComponent() {
  const user = useSessionUser()
  const href = useLocation({ select: (location) => location.href })
  // Signed out: through login and back to this very request, its query intact.
  const search = Route.useSearch()
  if (!user) return <Navigate to="/login" search={{ redirect: href }} replace />
  // Not a request any client sends; the server would refuse its shape before reading it.
  if (!search.client_id || !search.redirect_uri || !search.code_challenge) return <Unusable reason={null} />
  return <Consent />
}

function Unusable(props: { reason: string | null }) {
  const { t, i18n } = useLingui()
  const message = props.reason ? errorCodeMessage(props.reason) : undefined
  return (
    <AuthCard
      home
      title={t`This AI app can’t be connected`}
      subtitle={
        message ? i18n._(message) : t`The link it opened is incomplete. Start the connection again from the app.`
      }
    >
      {null}
    </AuthCard>
  )
}

function Consent() {
  const { t, i18n } = useLingui()
  const { context } = useMatch({ from: '/_public/oauth/authorize/_request' })
  const { data: view } = useSuspenseQuery(context.authorizationQueryOptions)
  const approve = useMutation(trpc.mcp.approve.mutationOptions())
  const deny = useMutation(trpc.mcp.deny.mutationOptions())
  const [leaving, setLeaving] = useState(false)
  const request = context.authorizationRequest

  // A full-page navigation: the app's redirect is another origin, or its own scheme.
  const answer = async (approved: boolean) => {
    setLeaving(true)
    try {
      const { redirectTo } = await (approved ? approve : deny).mutateAsync(request)
      window.location.assign(redirectTo)
    } catch {
      setLeaving(false)
    }
  }

  if (view.status === 'refused') return <Unusable reason={view.code} />

  const { clientName, redirectHost, acting } = view
  const company = acting.companyName
  const actingAs =
    acting.view === 'company'
      ? company
        ? t`as ${company}, with the company’s data you can see`
        : t`as your company, with the company’s data you can see`
      : company
        ? t`as yourself, in ${company}, with only what you can see there`
        : t`as yourself, with only your own data`
  const failed = approve.error ?? deny.error
  const failure = failed ? errorCodeMessage(failed.message) : undefined

  return (
    <AuthCard
      home
      title={t`Connect ${clientName} to D’Anchiano?`}
      subtitle={t`It will act ${actingAs}.`}
      contentClassName="flex flex-col gap-5"
    >
      <ul className="text-muted-foreground flex list-disc flex-col gap-2 pl-5 text-sm">
        <li>{t`${clientName} will read the people, positions, offers and teams you can see, with their real names and scores. Its AI provider receives them.`}</li>
        <li>{t`After asking you, it can add candidates to offers, create teams, add members and invite employees.`}</li>
        <li>{t`You can disconnect it at any time from your account menu, under Connected AI apps.`}</li>
      </ul>
      <p className="text-muted-foreground text-xs">{t`You will be sent back to ${redirectHost}.`}</p>
      {failed && <p className="text-destructive text-sm">{failure ? i18n._(failure) : failed.message}</p>}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" disabled={leaving} onClick={() => void answer(false)}>
          {t`Deny`}
        </Button>
        <Button className="flex-1" disabled={leaving} onClick={() => void answer(true)}>
          {t`Approve`}
        </Button>
      </div>
    </AuthCard>
  )
}
